import React, { useEffect, useState } from "react";
import ClientDeadlinesCard from '../../components/ClientDeadlinesCard/ClientDeadlinesCard';
import {
  Container,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Box,
  Divider,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Tabs,
  Tab
} from "@mui/material";
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import EventIcon from '@mui/icons-material/Event';
import { useSearchParams, useNavigate } from "react-router-dom";
import Nav from "../../components/Nav/Nav";
import { ContextTicketDto, ticketStatusNames, TicketStatusNameType } from "../HomeView/HomeView";
import {
  formatGenderLabel,
  formatPronounSetLabel,
} from "../../utils/ticketGenderPronouns";
import CourtEventApi, { CourtEventDto } from "../../api/CourtEventApi";
import "./TicketView.scss";
import { getStatusColor } from "../../utils/GetStatusColor";
import ClientProgressBar from "../../components/ClientProgressBar/ClientProgressBar";
import {
  clientStageForStatus,
  describeStatusForClient,
} from "../../components/CaseStatusTimeline/clientStages";
import { getStatusIcon } from "../../utils/GetStatusIcon";

const TicketView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<ContextTicketDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Court Events state
  const [courtEvents, setCourtEvents] = useState<CourtEventDto[]>([]);
  const [courtEventsLoading, setCourtEventsLoading] = useState(false);
  const [courtEventError, setCourtEventError] = useState<string | null>(null);

  const ticketGuid = searchParams.get('Ticket_guid');

  useEffect(() => {
    if (!ticketGuid) {
      setError('No ticket specified');
      setLoading(false);
      return;
    }
    fetchTicket();
  }, [ticketGuid]);

  useEffect(() => {
    if (ticket && activeTab === 1) {
      loadCourtEvents();
    }
  }, [ticket, activeTab]);

  const loadCourtEvents = async () => {
    if (!ticket) return;

    setCourtEventsLoading(true);
    setCourtEventError(null);

    try {
      const events = await CourtEventApi.listCourtEvents({ 
        Ticket_guid: ticket.ticket.Ticket_guid 
      });
      setCourtEvents(events);
    } catch (error) {
      setCourtEventError(error instanceof Error ? error.message : 'Failed to load court events');
    } finally {
      setCourtEventsLoading(false);
    }
  };

  const fetchTicket = async () => {
    if (!ticketGuid) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${process.env.API_HOST || ''}/api/v1/tickets?Ticket_guid=${ticketGuid}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ticket: ${response.status} ${response.statusText}`);
      }

      const ticketsData: ContextTicketDto[] = await response.json();
      
      const matchedTicket = ticketsData.find(
        (item) => item.ticket.Ticket_guid === ticketGuid,
      );

      if (!matchedTicket) {
        throw new Error('Ticket not found');
      }

      setTicket(matchedTicket);
    } catch (err) {
      console.error("Error fetching ticket:", err);
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatDateShort = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatEpochDate = (epochTime: number) => {
    try {
      return new Date(parseInt('' + epochTime)).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatEpochTime = (epochTime: number) => {
    try {
      return new Date(parseInt('' + epochTime)).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Invalid time';
    }
  };

  const formatTime = (timeString: string) => {
    try {
      if (!timeString) return 'Not specified';
      
      // Handle HH:MM format
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const minute = parseInt(minutes);
      
      if (isNaN(hour) || isNaN(minute)) return timeString;
      
      const date = new Date();
      date.setHours(hour, minute);
      
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  // TabPanel component for managing tabs
  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`ticket-tabpanel-${index}`}
        aria-labelledby={`ticket-tab-${index}`}
      >
        {value === index && children}
      </div>
    );
  };

  if (!ticketGuid) {
    return (
      <>
        <Nav />
        <Container maxWidth="lg" className="ticket-view">
          <Alert severity="error" sx={{ mt: 2 }}>
            No ticket specified. Please select a ticket from the tickets list.
          </Alert>
        </Container>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Nav />
        <Container maxWidth="lg" className="ticket-view">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>Loading ticket...</Typography>
          </Box>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Nav />
        <Container maxWidth="lg" className="ticket-view">
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/tickets')}
              variant="outlined"
            >
              Back to Tickets
            </Button>
          </Box>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Container>
      </>
    );
  }

  if (!ticket) {
    return null;
  }

  return (
    <>
      <Nav />
      <Container maxWidth="lg" className="ticket-view">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/tickets')}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            Back to Tickets
          </Button>
          
          <Typography variant="h4" component="h1" className="ticket-view-title">
            Ticket #{ticket.ticket.Ticket_Number}
          </Typography>
        </Box>

        <Card className="ticket-card">
          <CardContent>
            {/* Ticket Header */}
            <Box className="ticket-header">
              <Box className="ticket-title">
                <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h5" component="h2">
                  Ticket #{ticket.ticket.Ticket_Number}
                </Typography>
              </Box>
              {/*
                Lloyd, Aug 19: a simpler view for the client. The colour and icon still key off the
                internal status — those lookups are exhaustive over it — but the words the client
                reads are their own. This chip used to say "court respond by date satisfied
                (Pending)".
              */}
              <Chip
                label={
                  describeStatusForClient(ticket.ticketStatus?.TicketStatus_Name) ??
                  'In progress'
                }
                color={getStatusColor(ticket.ticketStatus?.TicketStatus_Name || ticketStatusNames[0])}
                icon={getStatusIcon(ticket.ticketStatus?.TicketStatus_Name || ticketStatusNames[0])}
                size="medium"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/*
              The eight-stage view (Lloyd, Aug 19). The client portal had no progress view at all —
              only the status chip above and a history list, both printing internal status names.
              Deliberately the simple bar and not the staff chart: he wants all thirty-nine steps on
              the firm's side and a simpler view on the client's.
            */}
            <Box sx={{ mb: 3 }}>
              <ClientProgressBar
                currentStageIndex={clientStageForStatus(
                  ticket.ticketStatus?.TicketStatus_Name,
                )}
                matterType={ticket.ticket.Ticket_MatterType}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* The client's own deadlines (Lloyd, 6 Oct 2026). Above the tabs, so it is seen without
                looking for it; it draws nothing when the case has no conditions. */}
            <ClientDeadlinesCard ticketGuid={ticket.ticket.Ticket_guid} />

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={activeTab} 
                onChange={(event, newValue) => setActiveTab(newValue)}
                aria-label="ticket tabs"
              >
                <Tab label="Ticket Details" />
                <Tab label="Court Events" icon={<EventIcon />} />
              </Tabs>
            </Box>

            {/* Tab Panel 0 - Ticket Details */}
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {/* Ticket Information */}
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Ticket Information
                  </Typography>
                  
                  <Box className="ticket-info-grid">
                    <Box className="info-item">
                      <Typography variant="subtitle2" color="text.secondary">
                        Citation Number
                      </Typography>
                      <Typography variant="body1">
                        {ticket.ticket.Ticket_Citation}
                      </Typography>
                    </Box>

                    <Box className="info-item">
                      <Typography variant="subtitle2" color="text.secondary">
                        Location
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          {ticket.ticket.Ticket_Address}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {ticket.ticket.Ticket_City}, {ticket.ticket.Ticket_County}
                      </Typography>
                    </Box>

                    {ticket.ticket.Ticket_CourtDate && (
                      <Box className="info-item">
                        <Typography variant="subtitle2" color="text.secondary">
                          Initial Court Date
                        </Typography>
                        <Box display="flex" alignItems="center">
                          <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body1">
                            {formatDateShort(ticket.ticket.Ticket_CourtDate)}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Box className="info-item">
                      <Typography variant="subtitle2" color="text.secondary">
                        License Type
                      </Typography>
                      <Typography variant="body1">
                        {ticket.ticket.Ticket_LicenseType}
                      </Typography>
                    </Box>

                    {(ticket.ticket.Ticket_DriversLicenseNumber ||
                      ticket.ticket.Ticket_Gender ||
                      ticket.ticket.Ticket_HasTakenDscInPastYear !== null &&
                        ticket.ticket.Ticket_HasTakenDscInPastYear !== undefined) && (
                      <>
                        {ticket.ticket.Ticket_DriversLicenseNumber && (
                          <Box className="info-item">
                            <Typography variant="subtitle2" color="text.secondary">
                              Driver&apos;s License #
                            </Typography>
                            <Typography variant="body1">
                              {ticket.ticket.Ticket_DriversLicenseNumber}
                            </Typography>
                          </Box>
                        )}
                        {ticket.ticket.Ticket_Gender && (
                          <Box className="info-item">
                            <Typography variant="subtitle2" color="text.secondary">
                              Gender
                            </Typography>
                            <Typography variant="body1">
                              {formatGenderLabel(ticket.ticket.Ticket_Gender)}
                              {formatPronounSetLabel(ticket.ticket.Ticket_PronounSet)
                                ? ` · ${formatPronounSetLabel(ticket.ticket.Ticket_PronounSet)}`
                                : ''}
                            </Typography>
                          </Box>
                        )}
                        {ticket.ticket.Ticket_HasTakenDscInPastYear !== null &&
                          ticket.ticket.Ticket_HasTakenDscInPastYear !== undefined && (
                          <Box className="info-item">
                            <Typography variant="subtitle2" color="text.secondary">
                              DSC in past year
                            </Typography>
                            <Typography variant="body1">
                              {ticket.ticket.Ticket_HasTakenDscInPastYear ? 'Yes' : 'No'}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    <Box className="info-item">
                      <Typography variant="subtitle2" color="text.secondary">
                        Representation Plan
                      </Typography>
                      <Typography variant="body1">
                        {ticket.ticket.Ticket_Representation}
                      </Typography>
                    </Box>

                    <Box className="info-item">
                      <Typography variant="subtitle2" color="text.secondary">
                        Zone
                      </Typography>
                      <Typography variant="body1">
                        {ticket.ticket.Ticket_Zone}
                      </Typography>
                    </Box>

                    <Box className="info-item">
                      <Typography variant="subtitle2" color="text.secondary">
                        Submitted
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(ticket.ticket.Ticket_CreatedAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Status Information and History */}
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Status Information
                  </Typography>

                  {/* Current Status */}
                  <Paper className="current-status" elevation={1}>
                    <Box display="flex" alignItems="center" mb={1}>
                      {getStatusIcon(ticket.ticketStatus?.TicketStatus_Name || ticketStatusNames[0])}
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {describeStatusForClient(ticket.ticketStatus?.TicketStatus_Name) ??
                          'In progress'}
                      </Typography>
                    </Box>
                    {ticket.ticketStatus?.TicketStatus_Desc && (
                      <Typography variant="body2" color="text.secondary">
                        {ticket.ticketStatus.TicketStatus_Desc}
                      </Typography>
                    )}
                    {ticket.ticketStatus?.TicketStatus_DateTime && (
                      <Typography variant="caption" color="text.secondary">
                        Updated: {formatDate(ticket.ticketStatus.TicketStatus_DateTime)}
                      </Typography>
                    )}
                  </Paper>

                  {/* Status History */}
                  {ticket.statusHistory && ticket.statusHistory.length > 0 && (
                    <Box className="status-history" mt={3}>
                      <Typography variant="subtitle1" gutterBottom>
                        Status History
                      </Typography>
                      <List>
                        {ticket.statusHistory
                          .sort((a, b) => new Date(b.TicketStatus_DateTime).getTime() - new Date(a.TicketStatus_DateTime).getTime())
                          .map((status) => (
                          <ListItem key={status.TicketStatus_guid} sx={{ py: 1, px: 0 }}>
                            <ListItemIcon>
                              <Chip
                                size="small"
                                color={getStatusColor(status.TicketStatus_Name)}
                                icon={getStatusIcon(status.TicketStatus_Name)}
                                label={
                                  describeStatusForClient(status.TicketStatus_Name) ??
                                  'Update'
                                }
                              />
                            </ListItemIcon>
                            {/*
                              The description is staff-written and case-specific, so it stays as the
                              primary line when there is one. What is gone is falling back to the
                              internal status name, which is what put "citation received
                              (Approving)" in front of clients.
                            */}
                            <ListItemText
                              primary={
                                status.TicketStatus_Desc ||
                                describeStatusForClient(status.TicketStatus_Name) ||
                                'Case updated'
                              }
                              secondary={formatDate(status.TicketStatus_DateTime)}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              </Box>
            </TabPanel>

            {/* Tab Panel 1 - Court Events */}
            <TabPanel value={activeTab} index={1}>
              <Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  Court Events
                </Typography>

                {courtEventError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {courtEventError}
                  </Alert>
                )}

                {courtEventsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : courtEvents.length > 0 ? (
                  <Stack spacing={2}>
                    {courtEvents.map((event) => (
                      <Card 
                        key={event.CourtEvent_guid} 
                        sx={{ 
                          border: '1px solid', 
                          borderColor: 'divider',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <CardContent>
                          <Stack spacing={2}>
                            <Stack direction="row" spacing={2}>
                              <Box flex={1}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Date
                                </Typography>
                                <Typography variant="body1">
                                  {formatEpochDate(event.CourtEvent_Start)}
                                </Typography>
                              </Box>
                              
                              <Box flex={1}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Start Time
                                </Typography>
                                <Typography variant="body1">
                                  {formatEpochTime(event.CourtEvent_Start)}
                                </Typography>
                              </Box>
                              
                              <Box flex={1}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  End Time
                                </Typography>
                                <Typography variant="body1">
                                  {formatEpochTime(event.CourtEvent_End)}
                                </Typography>
                              </Box>
                              
                              <Box flex={1}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Event ID
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                  {event.CourtEvent_guid}
                                </Typography>
                              </Box>
                            </Stack>

                            {event.CourtEvent_Title && (
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Title
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {event.CourtEvent_Title}
                                </Typography>
                              </Box>
                            )}

                            {event.CourtEvent_Desc && (
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Description
                                </Typography>
                                <Typography variant="body2">
                                  {event.CourtEvent_Desc}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box textAlign="center" py={4}>
                    <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      No court events scheduled
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check back later for court hearing dates and appointments.
                    </Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default TicketView;
